import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Copy, Trash2, Power } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APIKeysManagement = ({ getAuthHeader }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, {
        headers: getAuthHeader()
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api-keys`, formData, {
        headers: getAuthHeader()
      });
      setNewKey(response.data);
      setFormData({ name: '', description: '' });
      setShowCreateForm(false);
      fetchAPIKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Error creating API key: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (window.confirm('Delete this API key? This action cannot be undone.')) {
      try {
        await axios.delete(`${API}/api-keys/${keyId}`, {
          headers: getAuthHeader()
        });
        fetchAPIKeys();
      } catch (error) {
        console.error('Error deleting API key:', error);
      }
    }
  };

  const handleToggleKey = async (keyId) => {
    try {
      await axios.put(`${API}/api-keys/${keyId}/toggle`, {}, {
        headers: getAuthHeader()
      });
      fetchAPIKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API Keys for Agents</h2>
          <p className="text-gray-600">Create API keys to allow AI agents to publish articles automatically</p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus size={16} className="mr-2" />
            Create API Key
          </Button>
        )}
      </div>

      {/* New Key Alert */}
      {newKey && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800">✅ API Key Created Successfully!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key:</label>
                <div className="flex gap-2">
                  <Input
                    value={newKey.key}
                    readOnly
                    className="font-mono text-sm bg-white"
                  />
                  <Button onClick={() => copyToClipboard(newKey.key)} variant="outline">
                    <Copy size={16} />
                  </Button>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Important:</strong> Save this API key now! You won't be able to see it again.
                </p>
              </div>
              <Button onClick={() => setNewKey(null)} variant="outline" className="w-full">
                I've Saved the Key
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New API Key</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., AI Research Agent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What will this key be used for?"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">Create</Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>Active API Keys ({apiKeys.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {apiKeys.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No API keys yet. Create one to allow agents to publish articles.
              </p>
            ) : (
              apiKeys.map((key) => (
                <div
                  key={key.id}
                  className={`border rounded-lg p-4 ${
                    key.is_active ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900">{key.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          key.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {key.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {key.description && (
                        <p className="text-sm text-gray-600 mb-2">{key.description}</p>
                      )}
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>Key: {key.key.substring(0, 20)}...</span>
                        <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                        {key.last_used && (
                          <span>Last used: {new Date(key.last_used).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4">
                      <button
                        onClick={() => copyToClipboard(key.key)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Copy Key"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleKey(key.id)}
                        className={`p-2 rounded ${
                          key.is_active
                            ? 'text-orange-600 hover:bg-orange-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={key.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documentation */}
      <Card>
        <CardHeader>
          <CardTitle>📖 How to Use API Keys</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-bold mb-2">Publish Single Article:</h4>
            <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-x-auto text-xs">
{`curl -X POST "${BACKEND_URL}/api/publish/article?api_key=YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "AI Research Breakthrough",
    "description": "New findings in AI research",
    "category": "Research Paper",
    "readTime": "10 min",
    "publishDate": "Feb 2026",
    "imageUrl": "https://example.com/image.jpg",
    "articleUrl": "https://example.com/article"
  }'`}
            </pre>
          </div>

          <div>
            <h4 className="font-bold mb-2">Python Example:</h4>
            <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-x-auto text-xs">
{`import requests

api_key = "YOUR_API_KEY"
url = "${BACKEND_URL}/api/publish/article"

article = {
    "title": "AI Research Breakthrough",
    "description": "New findings",
    "category": "Research Paper",
    "readTime": "10 min",
    "publishDate": "Feb 2026"
}

response = requests.post(
    url,
    params={"api_key": api_key},
    json=article
)
print(response.json())`}
            </pre>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded">
            <p className="text-sm text-blue-800">
              <strong>💡 Tip:</strong> You can integrate this with AI agents like ChatGPT, Claude, or custom automation scripts to automatically publish research articles!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APIKeysManagement;
